/**
 * Página principal de gestión de roles
 * Sistema Klever Octopus - Betel Natural
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiSearch, FiX, FiAlertTriangle, FiSettings,
  FiEye, FiEdit, FiCopy, FiTrash2, FiShield, FiChevronLeft, FiChevronRight, FiLoader
} from 'react-icons/fi';
import {
  useRoles
} from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal, Modal } from '../../components/UI/Modal';
import { Loading } from '../../components/UI/Loading';
import RoleDetailModal from './RoleDetailModal';

const Roles = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  // Estados locales
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Hooks para datos
  const {
    roles,
    loading,
    error,
    pagination,
    filters,
    deleteRole,
    duplicateRole,
    updateFilters,
    changePage,
    changeItemsPerPage,
    clearFilters,
    setError
  } = useRoles();

  // Verificar permisos
  const canCreate = hasPermission('manage_roles') || hasPermission('create_role');
  const canEdit = hasPermission('manage_roles') || hasPermission('edit_role');
  const canDelete = hasPermission('manage_roles') || hasPermission('delete_role');

  // Manejadores de eventos
  const handleCreateRole = () => {
    navigate('/system/admin/roles/nuevo');
  };

  const handleEditRole = (role) => {
    navigate(`/system/admin/roles/${role.id}/editar`);
  };

  const handleViewRole = (role) => {
    setSelectedRole(role);
    setShowDetailModal(true);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const handleDuplicateRole = (role) => {
    setSelectedRole(role);
    setShowDuplicateModal(true);
  };

  const confirmDelete = async () => {
    const result = await deleteRole(selectedRole.id);
    
    if (result.success) {
      setShowDeleteModal(false);
      setSelectedRole(null);
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const confirmDuplicate = async (newRoleData) => {
    const result = await duplicateRole(selectedRole.id, newRoleData);
    
    if (result.success) {
      setShowDuplicateModal(false);
      setSelectedRole(null);
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleSearch = (searchTerm) => {
    updateFilters({ search: searchTerm });
  };

  const handleStatusFilter = (status) => {
    updateFilters({ is_active: status });
  };

  const handleSort = (field) => {
    const newOrder = filters.sort_by === field && filters.sort_order === 'asc' ? 'desc' : 'asc';
    updateFilters({ sort_by: field, sort_order: newOrder });
  };

  const getRoleLevelBadge = (nivel) => {
    const levels = {
      1: { text: 'Básico', class: 'level-basic' },
      2: { text: 'Intermedio', class: 'level-intermediate' },
      3: { text: 'Avanzado', class: 'level-advanced' },
      4: { text: 'Admin', class: 'level-admin' }
    };
    
    const level = levels[nivel] || { text: 'N/A', class: 'level-default' };
    return (
      <span className={`level-badge ${level.class}`}>
        {level.text}
      </span>
    );
  };

  if (loading && (!roles || roles.length === 0)) {
    return <Loading message="Cargando roles..." />;
  }

  return (
    <div className="roles-page">
      {/* Header */}
      <h1 className="page-title">ROLES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          {canCreate && (
            <button className="menu-item menu-item--first" onClick={handleCreateRole}>
              + AGREGAR ROL
            </button>
          )}

          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE DEL ROL"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <div className="menu-item menu-item--secondary" style={{
            flex: '0 0 auto',
            minWidth: '180px',
            padding: '0 20px'
          }}>
            <select
              value={filters.is_active}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="search-input"
              style={{
                width: '100%',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              <option value="">TODOS LOS ESTADOS</option>
              <option value="true">ACTIVOS</option>
              <option value="false">INACTIVOS</option>
            </select>
          </div>

          <button
            className="menu-item menu-item--warning"
            onClick={clearFilters}
            style={{
              flex: '0 0 auto',
              minWidth: '140px',
              gap: '8px'
            }}
          >
            <FiX />
            LIMPIAR
          </button>
        </div>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="alert alert-error">
          <FiAlertTriangle />
          {error}
          <button 
            className="btn btn-link"
            onClick={() => setError(null)}
          >
            <FiX />
          </button>
        </div>
      )}

      {/* Tabla de roles */}
      <div className="roles-table-container" style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        marginBottom: '24px'
      }}>
        <table className="roles-table" style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{
              borderBottom: '2px solid #e5e7eb',
              background: '#f9fafb'
            }}>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '40px'
              }}></th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>Nombre del Rol</th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>Descripción</th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '120px'
              }}>Nivel</th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '100px'
              }}>Usuarios</th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '100px'
              }}>Permisos</th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '100px'
              }}>Estado</th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '200px'
              }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {roles && roles.map((role, index) => (
              <tr key={role.id} style={{
                borderBottom: '1px solid #f3f4f6',
                transition: 'background 0.2s ease',
                background: index % 2 === 0 ? 'white' : '#fafbfc'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafbfc'}
              >
                {/* Indicador de color */}
                <td style={{ padding: '14px 16px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    backgroundColor: role.color || '#7B2D8E',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}></div>
                </td>

                {/* Nombre del rol */}
                <td style={{
                  padding: '14px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {role.display_name || role.name}
                    {role.is_system && (
                      <span style={{
                        background: '#e0e7ff',
                        color: '#4338ca',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <FiSettings size={10} />
                        Sistema
                      </span>
                    )}
                  </div>
                </td>

                {/* Descripción */}
                <td style={{
                  padding: '14px 16px',
                  fontSize: '13px',
                  color: '#6b7280',
                  maxWidth: '300px'
                }}>
                  {role.description || 'Sin descripción'}
                </td>

                {/* Nivel */}
                <td style={{
                  padding: '14px 16px',
                  textAlign: 'center'
                }}>
                  {getRoleLevelBadge(role.nivel_acceso)}
                </td>

                {/* Usuarios */}
                <td style={{
                  padding: '14px 16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827'
                }}>
                  {role.users_count || 0}
                </td>

                {/* Permisos */}
                <td style={{
                  padding: '14px 16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#7B2D8E'
                }}>
                  {role.permissions?.length || 0}
                </td>

                {/* Estado */}
                <td style={{
                  padding: '14px 16px',
                  textAlign: 'center'
                }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    background: role.is_active ? '#d1fae5' : '#fee2e2',
                    color: role.is_active ? '#065f46' : '#991b1b'
                  }}>
                    <span style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: role.is_active ? '#10b981' : '#ef4444'
                    }}></span>
                    {role.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>

                {/* Acciones */}
                <td style={{
                  padding: '14px 16px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleViewRole(role)}
                      title="Ver detalles"
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FiEye size={14} />
                      Ver
                    </button>

                    {canEdit && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleEditRole(role)}
                        title="Editar"
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <FiEdit size={14} />
                      </button>
                    )}

                    {canDelete && !role.is_system && role.users_count === 0 && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteRole(role)}
                        title="Eliminar"
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Estado vacío */}
      {(!roles || roles.length === 0) && !loading && (
        <div className="empty-state">
          <FiShield />
          <h3>No se encontraron roles</h3>
          <p>No hay roles que coincidan con los filtros aplicados.</p>
          {canCreate && (
            <button 
              className="btn btn-primary"
              onClick={handleCreateRole}
            >
              Crear primer rol
            </button>
          )}
        </div>
      )}

      {/* Paginación */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Mostrando {roles?.length || 0} de {pagination.totalItems} roles
          </div>
          
          <div className="pagination-controls">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              <FiChevronLeft />
              Anterior
            </button>

            <span className="pagination-info">
              Página {pagination.currentPage} de {pagination.totalPages}
            </span>

            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => changePage(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Siguiente
              <FiChevronRight />
            </button>
          </div>

          <select
            value={pagination.itemsPerPage}
            onChange={(e) => changeItemsPerPage(parseInt(e.target.value))}
            className="form-control pagination-select"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
          </select>
        </div>
      )}

      {/* Modales */}
      {showDetailModal && selectedRole && (
        <RoleDetailModal
          role={selectedRole}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedRole(null);
          }}
          onEdit={canEdit ? () => {
            setShowDetailModal(false);
            handleEditRole(selectedRole);
          } : null}
        />
      )}

      {showDeleteModal && selectedRole && (
        <ConfirmModal
          isOpen={showDeleteModal}
          title="Confirmar eliminación"
          message={`¿Estás seguro de que deseas eliminar el rol "${selectedRole.display_name || selectedRole.name}"?`}
          confirmText="Eliminar"
          confirmClass="btn-danger"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedRole(null);
          }}
        />
      )}

      {showDuplicateModal && selectedRole && (
        <Modal
          title={`Duplicar Rol - ${selectedRole.display_name || selectedRole.name}`}
          isOpen={showDuplicateModal}
          onClose={() => {
            setShowDuplicateModal(false);
            setSelectedRole(null);
          }}
          size="medium"
        >
          <DuplicateRoleForm
            originalRole={selectedRole}
            onSubmit={confirmDuplicate}
            onCancel={() => {
              setShowDuplicateModal(false);
              setSelectedRole(null);
            }}
          />
        </Modal>
      )}

      {loading && (
        <div className="loading-overlay">
          <Loading message="Procesando..." />
        </div>
      )}
    </div>
  );
};

// Componente para duplicar rol
const DuplicateRoleForm = ({ originalRole, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: `${originalRole.name}_copy`,
    display_name: `${originalRole.display_name || originalRole.name} (Copia)`,
    description: `Copia de ${originalRole.display_name || originalRole.name}`
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.display_name.trim()) {
      newErrors.display_name = 'El nombre para mostrar es requerido';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setSubmitting(true);
      await onSubmit(formData);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="duplicate-role-form">
      <div className="form-group">
        <label htmlFor="name" className="required">Nombre del Rol (Interno)</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
          placeholder="nombre_rol"
        />
        {errors.name && (
          <div className="invalid-feedback">{errors.name}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="display_name" className="required">Nombre para Mostrar</label>
        <input
          type="text"
          id="display_name"
          name="display_name"
          value={formData.display_name}
          onChange={handleChange}
          className={`form-control ${errors.display_name ? 'is-invalid' : ''}`}
          placeholder="Nombre del Rol"
        />
        {errors.display_name && (
          <div className="invalid-feedback">{errors.display_name}</div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="description">Descripción</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows="3"
          placeholder="Descripción del rol duplicado"
        />
      </div>

      <div className="modal-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
        
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <FiLoader className="spin" />
              Duplicando...
            </>
          ) : (
            <>
              <i className="fas fa-copy"></i>
              Duplicar Rol
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default Roles;
